import { yupResolver } from '@hookform/resolvers/yup';
import { useContext, useRef, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import * as Yup from 'yup';
import { useParams } from 'react-router-dom';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { LangContext } from '@/context/LangProvider';
import { useMutateRenameWorkspace } from '@/reactQuery/post';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { showToastErrorMessage } from '@/utils/showToastErrorMessage';
import ModalToastContainer, { MODAL_TOAST_CONTAINER_ID } from '../ui/ModalToastContainer';
import ErrorMessage from '../error-message/ErrorMessage';

const validationSchema = Yup.object().shape({
  name: Yup.string().required('New workspace name is required'),
});

function RenameWorkspaceModal({ currentWorkspaceName }: { currentWorkspaceName: string }) {
  const { lang } = useContext(LangContext);

  const defaultValues = {
    name: currentWorkspaceName,
  };

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({ defaultValues, resolver: yupResolver(validationSchema) });
  const [openModal, setOpenModal] = useState(false);
  const { organization_id: organizationId, workspace_id: workspaceId } = useParams();
  const { mutateAsync } = useMutateRenameWorkspace(organizationId, workspaceId);
  const modalRef = useRef<HTMLDivElement>(null);

  const onSubmit: SubmitHandler<{ name: string }> = async ({ name }) => {
    try {
      await mutateAsync({ name: name.trim() });
      reset();
      setOpenModal(false);
      toast.success(lang.get('msg.workspaceSuccessfullyRenamed'));
    } catch (error) {
      showToastErrorMessage(error, { containerId: MODAL_TOAST_CONTAINER_ID });
    }
  };

  const hasErrorMessage = !!errors.name?.message;

  return (
    <AlertDialog open={openModal}>
      <AlertDialogTrigger onClick={() => setOpenModal(true)} asChild>
        <p className="py-1 pl-2 text-sm cursor-pointer hover:bg-secondary-background">
          {lang.get('msg.rename')}
        </p>
      </AlertDialogTrigger>
      <AlertDialogContent ref={modalRef} className="gap-0">
        <form onSubmit={handleSubmit(onSubmit)}>
          <AlertDialogHeader>
            <AlertDialogTitle>{lang.get('msg.renameYourWorkspace')}</AlertDialogTitle>
            <div className="flex flex-col pt-5 pb-12">
              <label className="relative flex flex-col w-full gap-1">
                <Input
                  {...register('name')}
                  placeholder="Enter new workspace name"
                  className="w-full"
                  hasError={hasErrorMessage}
                />
                {hasErrorMessage && <ErrorMessage message={errors.name?.message as string} />}
              </label>
            </div>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setOpenModal(false)}>
              {lang.get('msg.cancel')}
            </AlertDialogCancel>
            <Button variant="default" type="submit" disabled={isSubmitting}>
              {lang.get('msg.submit')}
            </Button>
          </AlertDialogFooter>
        </form>
        <ModalToastContainer targetRef={modalRef} />
      </AlertDialogContent>
    </AlertDialog>
  );
}

export default RenameWorkspaceModal;
